import React, { useState, useEffect, Fragment, useRef } from 'react';
import cn from 'classnames';
import ScoringRubrics from './ScoringRubrics';
import ScoringLearnosityItem from './ScoringLearnosityItem';
import { isMCQ } from 'legacy-src/utility/progressHelpers';
import LearnosityItemPreview from '../learnosity/LearnosityItemPreview';
import ScoringNavArrows from './ScoringNavArrows';
import { TABS } from './ScoringLayout';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';

function ScoringItem({
  question,
  questionIndex,
  questionsLength,
  visibleQuestionIndex,
  questionsWithApi,
  learnosityItems,
  studentResponseForReference,
  responseAnalysis,
  scoringStudent,
  goToQuestion,
  handleAutoSave,
  isLoading,
  isFinalExam,
  responseTabLabel,
  subjectData,
  isStudent,
  featureFlags,
  isPrintTest,
  sectionToGo,
  isMobile,
  renderNavArrows,
  setTabData,
  tabData,
  responsesPage,
  isRubricsDisabled,
  params,
}) {
  const [sectionIndex, setSectionIndex] = useState(0);

  const stimulusRef = useRef(null);
  const scoringQuidelinesRef = useRef(null);

  const goToPreviousScoringSection = () => {
    setSectionIndex(sectionIndex - 1);
    handleAutoSave();
  };
  const goToNextScoringSection = () => {
    setSectionIndex(sectionIndex + 1);
    handleAutoSave();
  };
  const onTabRubricChange = (index) => {
    setSectionIndex(index);
    handleAutoSave();
  };

  const isMCQCurrent = isMCQ(question);

  const isVisible = questionIndex === visibleQuestionIndex;

  useEffect(() => {
    isVisible && setSectionIndex(sectionToGo);
  }, [sectionToGo, visibleQuestionIndex]);

  const { learnosityItemReference } = question;
  const {
    reshape_two_column_questions: reshapeTwoColumnQuestions,
    reshape_two_column_questions_preap: reshapeTwoColumnQuestionsPreAP,
  } = featureFlags;

  const { isPreApSubject } = subjectData;

  const { selectedTabIndex, mobileTab } = tabData;

  /**
   * On tabs change event callback
   * @param {number} index The index of the tab
   * */
  const onTabsChange = (index) => {
    if (scoringQuidelinesRef.current && stimulusRef.current) {
      scoringQuidelinesRef.current.scrollIntoView();
      stimulusRef.current.scrollIntoView();
    }

    const container = index === 0 ? TABS.details : TABS.responses;
    setTabData({
      selectedTabIndex: index,
      mobileTab: container,
    });
  };

  const multipartTabName = `Question ${
    questionIndex + 1
  } / ${responseTabLabel}`;

  const scoringNavArrows = (
    <ScoringNavArrows
      rubrics={question && question.children}
      rubricIndex={sectionIndex}
      goToNextQuestion={() => goToQuestion(true)}
      goToPreviousQuestion={(goToLastRubric) =>
        goToQuestion(false, goToLastRubric)
      }
      gotToPreviousScoringSection={goToPreviousScoringSection}
      gotToNextScoringSection={goToNextScoringSection}
      hasNextQuestion={visibleQuestionIndex < questionsLength - 1}
      hasPreviousQuestion={visibleQuestionIndex > 0}
    />
  );

  useEffect(() => {
    if (isMobile && isVisible) {
      selectedTabIndex === 0 && setSectionIndex(0);
      renderNavArrows(scoringNavArrows);
    }
  }, [visibleQuestionIndex, sectionIndex, tabData]);

  return (
    <div
      id={`scoring-block-${question.id}`}
      className={cn('block', { '--offscreen': !isVisible })}
      aria-disabled={!isVisible}
      data-test-visible={isVisible}
      data-test-question={question.id}
      data-scoring-content="true"
    >
      <div
        ref={stimulusRef}
        className={cn(
          'content border-r border-solid border-gray-300 --with-scroll mobile:border-0',
          {
            '--offscreen': !isMCQCurrent && mobileTab !== TABS.details,
          },
        )}
      >
        {!isMCQCurrent && (
          <div className="mobile-tabs desktop:hidden bg-blue-500 text-center p-4.4">
            <ButtonWithIcon
              isSecondary
              background="white"
              icon="chevron_right"
              size="small"
              className="w-full justify-center"
              iconOrientation="right"
              iconClassName="fill-current scale-50"
              onClick={() => {
                onTabsChange(1);
              }}
            >
              View Scoring Guidelines
            </ButtonWithIcon>
          </div>
        )}

        <h1 className="title mobile:hidden">
          {!isStudent && !isFinalExam
            ? question.title
            : `Question ${questionIndex + 1}`}
        </h1>

        {isMCQCurrent ? (
          <Fragment>
            <h4 className="mcq-title text-center">
              {!isMobile ? multipartTabName : responseTabLabel}
            </h4>
            {
              <div className="scroll">
                <LearnosityItemPreview //only for student
                  isCurrent
                  areOptionsSelectable
                  isMCQ
                  isStudent
                  reference={learnosityItemReference}
                  learnosityQuestion={
                    questionsWithApi[
                      studentResponseForReference[learnosityItemReference]
                    ]
                  }
                  learnosityItem={learnosityItems[learnosityItemReference]}
                  responseAnalysis={responseAnalysis}
                  studentId={scoringStudent.id}
                  announceOptions={false}
                  shouldHideParts
                />
              </div>
            }
          </Fragment>
        ) : (
          <ScoringLearnosityItem
            isMobile={isMobile}
            responseTabLabel={responseTabLabel}
            isLoading={isLoading}
            questionId={question.id}
            questionIndex={questionIndex}
            reference={learnosityItemReference}
            isCurrent={isVisible}
            learnosityItem={learnosityItems[learnosityItemReference]}
            learnosityQuestion={
              questionsWithApi[
                studentResponseForReference[learnosityItemReference]
              ]
            }
            isPrintTest={isPrintTest}
            reshapeTwoColumnQuestions={
              isPreApSubject
                ? reshapeTwoColumnQuestionsPreAP
                : reshapeTwoColumnQuestions
            }
          />
        )}
      </div>

      {!isRubricsDisabled && (
        <div
          ref={scoringQuidelinesRef}
          className={cn(
            'content border-l border-solid border-gray-300 --with-scroll mobile:border-0',
            {
              '--offscreen': isMobile && mobileTab !== TABS.responses,
            },
          )}
        >
          {!isMCQCurrent && (
            <div className="desktop:hidden bg-blue-500 text-center p-4.4">
              <ButtonWithIcon
                isSecondary
                background="white"
                icon="chevron_left"
                size="small"
                className="w-full justify-center"
                iconClassName="fill-current scale-50"
                onClick={() => {
                  onTabsChange(0);
                }}
              >
                Back to Question
              </ButtonWithIcon>
            </div>
          )}

          <ScoringRubrics
            data-test-rubrics
            question={question}
            isMobile={isMobile}
            gotToNextScoringSection={goToNextScoringSection}
            onTabChange={onTabRubricChange}
            rubricIndex={sectionIndex}
            isLoading={isLoading}
            params={params}
            isScoringItem
          />

          {!isMobile && !responsesPage && scoringNavArrows}
        </div>
      )}
    </div>
  );
}

ScoringItem.defaultProps = {
  goToQuestion: () => {},
  isStudent: false,
  subjectData: {},
  featureFlags: {},
};

export default ScoringItem;
